import { FC, useState, Dispatch, SetStateAction, useMemo, useContext, useEffect } from 'react';
import axios from 'axios';
import { path } from 'ramda';

import { IFoodlaCategoryOption } from 'models/category';

import EditIcon from '@material-ui/icons/Edit';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CategoryTree from './category-tree';
import { OrgCategoryTreeType } from './types';
import IProduct, { IApoteaSuggestion, IIcaOnlineSuggestion } from 'models/product';
import { categoryTypeToStateField } from './helpers';
import restEndpoints from 'constants/restEndpoints';
import { KeycloakContext } from 'components/Secured';
import AutoFillCategoryTags, {
  mapApoteaAutoFillList,
  mapIcaInteralAutoFillList,
  mapIcaOnlineAutoFillList,
  TAutoFillData,
  TIcaInternalResponse,
} from './AutoFillCategoryTags';
import { IImage } from 'components/images/ImageSorter';

interface OrgCategoryProps {
  readOnly?: boolean;
  isFood?: boolean;
  sortedImages?: IImage[];
  label: string;
  loading: boolean;
  autoFill?: boolean;
  type: OrgCategoryTreeType;
  rootCategory?: IFoodlaCategoryOption;
  categories: IFoodlaCategoryOption[];
  state: IProduct;
  setState: Dispatch<SetStateAction<IProduct>>;
  dim?: boolean;
}

const OrgCategory: FC<OrgCategoryProps> = ({
  readOnly,
  isFood,
  sortedImages,
  label,
  loading,
  autoFill,
  type,
  rootCategory,
  categories,
  state,
  setState,
  dim,
}) => {
  const keycloakCtx = useContext(KeycloakContext);
  const token = path(['keycloak', 'token'], keycloakCtx) as string;

  const [editing, setEditing] = useState(false);
  const [loadingAutoFill, setLoadingAutoFill] = useState(false);
  const [calledAutoFill, setCalledAutoFill] = useState(false);
  const [autoFillDataList, setAutoFillDataList] = useState<TAutoFillData[]>([]);

  const [localSelectedCategoryId, setLocalSelectedCategoryId] = useState<string | null>(() => {
    return state[categoryTypeToStateField[type]] ?? null;
  });
  const localSelectedCategory = useMemo(() => {
    return categories.find(cat => cat.id === localSelectedCategoryId);
  }, [categories, localSelectedCategoryId]);

  const upstreamSelectedCategoryId = (() => {
    return state[categoryTypeToStateField[type]] ?? null;
  })();
  const setUpstreamSelectedCategoryId = (categoryId: string | null) => {
    setState(prevState => ({ ...prevState, [categoryTypeToStateField[type]]: categoryId }));
  };
  const upstreamSelectedCategory = useMemo(() => {
    return categories.find(cat => cat.id === upstreamSelectedCategoryId);
  }, [categories, upstreamSelectedCategoryId]);

  const handleUpdateClick = async () => {
    setEditing(false);
    setUpstreamSelectedCategoryId(localSelectedCategoryId);
  };

  const handleAutoFillClick = async () => {
    setLoadingAutoFill(true);
    setCalledAutoFill(false);

    try {
      const formData = new FormData();
      formData.append('categoryMode', type);
      formData.append('isFood', String(isFood));
      formData.append('product', JSON.stringify(state));
      if (sortedImages?.[0]?.pictureUrl) {
        formData.append('imageUrl', sortedImages[0].pictureUrl);
      }

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.forEach((value, key) => {
        if ((['pro', 'flash', 'fallback'].includes(key) && ['1', 'true'].includes(value)) || key === 'temperature') {
          formData.append(key, value);
        }
      });

      const { data } = await axios.post<{ data: TIcaInternalResponse | IIcaOnlineSuggestion }>(
        restEndpoints.aiOrgCategory,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      let result: TAutoFillData[] = [];

      if (type === 'apotea' && data?.data) {
        result = mapApoteaAutoFillList(data.data as IApoteaSuggestion, categories);
      }
      if (type === 'ica' && data?.data) {
        result = mapIcaInteralAutoFillList(data.data as TIcaInternalResponse, categories);
      }
      if (type === 'ica_online' && data?.data) {
        result = mapIcaOnlineAutoFillList(data.data as IIcaOnlineSuggestion, categories);
      }

      setAutoFillDataList(result);
    } catch (error: any) {
      console.error('Product review error:', error);
    } finally {
      setLoadingAutoFill(false);
      setCalledAutoFill(true);
    }
  };

  const handleUnsetClick = () => {
    setEditing(false);
    setUpstreamSelectedCategoryId(null);
    setLocalSelectedCategoryId(null);
  };

  const handleCancelClick = () => {
    setEditing(false);
    setLocalSelectedCategoryId(upstreamSelectedCategoryId);
  };

  useEffect(() => {
    if (type === 'ica_online' && state.icaOnlineCategorySuggestion) {
      setAutoFillDataList(mapIcaOnlineAutoFillList(state.icaOnlineCategorySuggestion, categories));
      setCalledAutoFill(true);
    }
  }, [type, state.icaOnlineCategorySuggestion, categories]);

  useEffect(() => {
    if (type === 'apotea' && state.apoteaCategorySuggestion) {
      setAutoFillDataList(mapApoteaAutoFillList(state.apoteaCategorySuggestion, categories));
      setCalledAutoFill(true);
    }
  }, [type, state.apoteaCategorySuggestion, categories]);

  return (
    <div style={{ marginBottom: editing ? 8 : 0 }}>
      {/* TODO: merge with OrgCategoryDisplay */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          lineHeight: '26px',
          opacity: dim ? 0.5 : 1,
        }}
      >
        <div>
          <b>{label}</b>
          <span style={{ marginLeft: 3, color: upstreamSelectedCategory?.name ? 'inherit' : 'red' }}>
            {upstreamSelectedCategory?.name || 'Odefinierad'}
          </span>
        </div>

        {!editing && !readOnly && (
          <IconButton size="small" style={{ color: '#000' }} onClick={() => setEditing(x => !x)}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </div>

      {editing && (
        <div style={{ marginTop: 8 }}>
          <CategoryTree
            readOnly={readOnly}
            loading={loading}
            hideLabel
            type={type}
            selectedCategory={localSelectedCategory}
            rootCategory={rootCategory}
            categories={categories}
            onSelect={({ children, ...data }) => {
              setLocalSelectedCategoryId(data.id);
            }}
          />

          <div style={{ marginTop: 4 }}>
            <Button disabled={readOnly} size="small" variant="text" color="primary" style={{ marginRight: 8 }} onClick={handleCancelClick}>
              Avbryt
            </Button>
            <Button disabled={readOnly} size="small" variant="text" color="primary" style={{ marginRight: 8 }} onClick={handleUnsetClick}>
              Rensa
            </Button>
            {autoFill && (
              <Button
                disabled={readOnly || loadingAutoFill}
                size="small"
                variant="text"
                color="primary"
                style={{ marginRight: 8 }}
                onClick={handleAutoFillClick}
              >
                {loadingAutoFill && <CircularProgress size={16} style={{ marginRight: 8 }} />}
                {loadingAutoFill ? 'Genererar...' : 'Generera förslag'}
              </Button>
            )}
            <Button disabled={readOnly} size="small" variant="outlined" color="primary" onClick={handleUpdateClick}>
              Uppdatera mappning
            </Button>
          </div>

          {calledAutoFill && (
            <AutoFillCategoryTags
              readOnly={readOnly}
              selectedCategory={localSelectedCategory}
              list={autoFillDataList}
              onSelect={data => setLocalSelectedCategoryId(data?.id)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default OrgCategory;
